"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SiteSearchService = void 0;
const mfe_common_types_and_mocks_one_search_client_1 = require("@awaze/mfe-common.types-and-mocks.one-search-client");
const mfe_common_types_and_mocks_site_search_service_1 = require("@awaze/mfe-common.types-and-mocks.site-search-service");
const formatISO_1 = __importDefault(require("date-fns/formatISO"));
const parse_1 = __importDefault(require("date-fns/parse"));
const site_search_service_helper_1 = require("./site-search-service.helper");
const mapPropertiesListToCommon_1 = require("./mapPropertiesListToCommon");
const mapPropertyCoordinatesEndpointResponse_1 = require("./mapPropertyCoordinatesEndpointResponse");
const search_properties_utils_1 = require("./search-properties-utils");
class SiteSearchService {
    constructor(OneSearchClient, summariesApi, logger) {
        this.OneSearchClient = OneSearchClient;
        this.summariesApi = summariesApi;
        this.logger = logger;
    }
    async getListResults(searchCriteria, brand, language, elements) {
        const brandedSearchCriteria = (0, site_search_service_helper_1.filterSearchCriteriaByBrand)(searchCriteria, brand);
        const oneSearchParamString = SiteSearchService.getOneSearchPropertiesParamString(brandedSearchCriteria);
        return this.getPropertiesListResponse(oneSearchParamString, brand, language ?? 'en', elements);
    }
    async getCoordinatesResults(searchCriteria, brand, language) {
        const brandedSearchCriteria = (0, site_search_service_helper_1.filterSearchCriteriaByBrand)(searchCriteria, brand);
        const oneSearchParamString = SiteSearchService.getOneSearchPropertiesParamString(brandedSearchCriteria);
        const results = await this.OneSearchClient.getPropertiesCoordinatesResponse(oneSearchParamString);
        return (0, mapPropertyCoordinatesEndpointResponse_1.mapPropertyCoordinatesEndpointResponse)(results, language ?? 'en');
    }
    async getPropertiesListResponse(oneSearchParamString, brand, language, elements = ['USPS', 'REVIEW', 'DISPLAY_NAME']) {
        const propertiesResult = await this.OneSearchClient.getPropertiesListResponse(oneSearchParamString);
        const summariesResult = await this.getSummariesResponse(propertiesResult.searchResults.map((property) => property.code), elements.map((ITEM) => mfe_common_types_and_mocks_site_search_service_1.SUMMARY_QUERY_ELEMENTS[ITEM]));
        return (0, mapPropertiesListToCommon_1.mapPropertiesListToCommon)({
            oneSearchResponse: propertiesResult,
            summariesResponse: summariesResult,
            language,
            elements,
        });
    }
    async getSummariesResponse(codes, elements) {
        return this.summariesApi
            .getSummariesResponse({
            codes,
            elements,
        })
            .catch((error) => {
            this.handleUnsuccessfulSummariesResponse(error);
            return { summaries: [] };
        });
    }
    static buildSearchPropertiesParams(searchCriteria) {
        const { accommodationType, accommodationTypes, adult: adults, bathrooms, bedrooms, child: children, discounts, features, grade, infant: infants, maxPrice, minPrice, ne, nights, page, pets, propertyId, range, rating, regionId, locationTag, placesId, siteFeatures, siteId, propertyCodes, radiusCentre, radiusKm, sort, sref, start, sw, salesMarket, size, } = searchCriteria;
        const minMaxRating = searchCriteria.rating
            ? (0, site_search_service_helper_1.getMinMaxRating)(searchCriteria.rating)
            : false;
        const isDatedSearch = start && nights;
        const isNotNovasolBrand = !salesMarket;
        const chosenPageSize = size ?? site_search_service_helper_1.RESULTS_PAGE_SIZE;
        return {
            size: chosenPageSize,
            ...(page && { from: (page - 1) * chosenPageSize }),
            ...(sort && { sort: (0, search_properties_utils_1.mapSortValues)(sort) }),
            ...(adults && { adults }),
            ...(infants && { infants }),
            ...(children && { children }),
            ...(regionId && { regionId }),
            ...(locationTag && { locationTag }),
            ...(salesMarket && { salesMarket }),
            ...(placesId && { polygonId: placesId }),
            ...(isDatedSearch && {
                startDate: (0, formatISO_1.default)((0, parse_1.default)(start, site_search_service_helper_1.FUSION_DATE_FORMAT, new Date()), {
                    representation: 'date',
                }),
                lengthOfStay: nights,
                flexibility: range || 0,
            }),
            ...(isDatedSearch && minPrice && { minPrice }),
            ...(isDatedSearch && maxPrice && { maxPrice }),
            ...(pets && { pets }),
            ...(features &&
                features.length > 0 && {
                features,
            }),
            ...(bathrooms && {
                minBathrooms: (0, site_search_service_helper_1.bathroomMapper)(bathrooms),
            }),
            ...(bedrooms && {
                minBedrooms: (0, site_search_service_helper_1.bedroomMapper)(bedrooms),
            }),
            ...(sref && { sref }),
            ...(siteFeatures &&
                siteFeatures.length > 0 && {
                siteFeatures,
            }),
            ...(siteId && { siteCode: siteId }),
            ...(propertyCodes && propertyCodes.length > 0 && { propertyCodes }),
            ...(accommodationTypes &&
                accommodationTypes.length > 0 && {
                accommodationTypes,
            }),
            ...(accommodationType &&
                mfe_common_types_and_mocks_one_search_client_1.SITE_GROUP_OPTIONS[accommodationType] && {
                groups: [mfe_common_types_and_mocks_one_search_client_1.SITE_GROUP_OPTIONS[accommodationType]],
            }),
            ...(ne && { ne }),
            ...(sw && { sw }),
            ...(radiusCentre && { radiusCentre }),
            ...(radiusKm && { radiusKm: radiusKm.toString() }),
            ...(rating &&
                minMaxRating && {
                minReviewScore: parseFloat(minMaxRating[0]),
                maxReviewScore: parseFloat(minMaxRating[1]),
            }),
            ...(propertyId && { propertyCodes: [propertyId] }),
            ...(grade &&
                site_search_service_helper_1.GRADE_CAT_IDS[grade] && {
                minGrade: site_search_service_helper_1.GRADE_CAT_IDS[grade],
                ...(isNotNovasolBrand && { maxGrade: site_search_service_helper_1.GRADE_CAT_IDS[grade] }),
            }),
            ...(discounts && { onlyDiscounted: 'true' }),
        };
    }
    static getOneSearchPropertiesParamString(searchCriteria) {
        return new URLSearchParams({
            ...SiteSearchService.buildSearchPropertiesParams(searchCriteria),
        }).toString();
    }
    handleUnsuccessfulSummariesResponse(error) {
        this.logger?.error({
            message: error.message,
            attributes: {
                statusCode: 500,
                errors: error.message,
                meta: {
                    request: 'Summaries.getSummariesResponse',
                    referrer: 'SearchService.getResults',
                },
            },
        });
    }
}
exports.SiteSearchService = SiteSearchService;
//# sourceMappingURL=site-search-service.js.map